<template>
  <div 
    v-if="showConsent" 
    class="fixed bottom-0 left-0 right-0 z-[9999] bg-white/75 backdrop-blur-sm border-t border-gray-200 shadow-2xl transition-all duration-500 ease-in-out"
    :class="{ 'translate-y-full': !isVisible, 'translate-y-0': isVisible }"
  >
    <div class="container mx-auto px-3 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] max-w-6xl">
      <div class="flex flex-col gap-3">
        <!-- Content Section -->
        <div class="flex flex-col sm:flex-row sm:items-center gap-3">
          <div class="flex items-start gap-3 flex-1 min-w-0">
            <div class="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-[var(--color-primary)] rounded-full flex items-center justify-center">
              <Cookie class="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-base font-semibold text-gray-900 font-poppins mb-1">
                Používame cookies
              </h3>
              <p class="text-sm text-gray-600 font-poppins leading-snug">
                Nevyhnutné cookies zabezpečujú fungovanie obchodu. S vaším súhlasom použijeme aj analytické a marketingové.
              </p>
            </div>
          </div>

          <!-- Action Buttons: a full-width row under the text on mobile, beside it from sm up -->
          <div class="grid grid-cols-2 sm:flex gap-2 flex-shrink-0">
            <button
              @click="acceptAll"
              class="px-4 py-2 bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-primary-dark)] transition-colors font-medium font-poppins text-xs whitespace-nowrap"
            >
              Prijať všetko
            </button>
            <button
              @click="acceptNecessaryOnly"
              class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium font-poppins text-xs whitespace-nowrap"
            >
              Len nevyhnutné
            </button>
          </div>
        </div>
          
        <!-- Detailed Cookie Information -->
        <div v-if="showDetails" class="space-y-2">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <!-- Necessary Cookies -->
            <div class="bg-gray-50/75 backdrop-blur-sm rounded-lg p-3">
              <div class="flex items-center justify-between mb-1">
                <h4 class="font-medium text-gray-900 font-poppins text-xs">Nevyhnutné</h4>
                <span class="text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full font-medium">
                  Aktívne
                </span>
              </div>
              <p class="text-xs text-gray-600 font-poppins">
                Základné fungovanie a bezpečnosť.
              </p>
            </div>
            
            <!-- Analytics Cookies -->
            <div class="bg-gray-50/75 backdrop-blur-sm rounded-lg p-3">
              <div class="flex items-center justify-between mb-1">
                <h4 class="font-medium text-gray-900 font-poppins text-xs">Analytické</h4>
                <label class="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    v-model="preferences.analytics" 
                    class="sr-only peer"
                  >
                  <div class="w-7 h-4 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-[var(--color-primary)]"></div>
                </label>
              </div>
              <p class="text-xs text-gray-600 font-poppins">
                Analýza návštevnosti.
              </p>
            </div>
            
            <!-- Marketing Cookies -->
            <div class="bg-gray-50/75 backdrop-blur-sm rounded-lg p-3">
              <div class="flex items-center justify-between mb-1">
                <h4 class="font-medium text-gray-900 font-poppins text-xs">Marketingové</h4>
                <label class="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    v-model="preferences.marketing" 
                    class="sr-only peer"
                  >
                  <div class="w-7 h-4 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-[var(--color-primary)]"></div>
                </label>
              </div>
              <p class="text-xs text-gray-600 font-poppins">
                Relevantné reklamy.
              </p>
            </div>
          </div>
          
          <!-- Save Preferences Button for Details -->
          <div class="flex justify-center pt-2">
            <button 
              @click="savePreferences"
              class="px-4 py-2 border border-[var(--color-primary)] text-[var(--color-primary)] rounded-lg hover:bg-[var(--color-primary)] hover:text-white transition-colors font-medium font-poppins text-xs"
            >
              Uložiť voľby
            </button>
          </div>
        </div>
      </div>
      
      <!-- Privacy Policy Link -->
      <div class="mt-2 pt-2 border-t border-gray-100">
        <p class="text-xs text-gray-500 font-poppins text-center">
          <router-link :to="$pagePath('privacy')" class="text-[var(--color-primary)] hover:underline">
            Zásady ochrany údajov
          </router-link>
          •
          <router-link :to="$pagePath('terms')" class="text-[var(--color-primary)] hover:underline">
            Obchodné podmienky
          </router-link>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import { Cookie } from 'lucide-vue-next'
import { useCookieConsent } from '@/composables/useCookieConsent'

const {
  showConsent,
  preferences,
  acceptAll,
  acceptNecessaryOnly,
  savePreferences,
  hasConsent
} = useCookieConsent()

const showDetails = ref(false)
const isVisible = ref(false)

const toggleDetails = () => {
  showDetails.value = !showDetails.value
}

onMounted(() => {
  // Show the banner with a slight delay for smooth animation
  if (showConsent.value) {
    nextTick(() => {
      setTimeout(() => {
        isVisible.value = true
      }, 100)
    })
  }
})
</script>

<style scoped>
/* Custom toggle switch styling is handled via Tailwind classes */
</style>
